<!-- frontend/src/components/NavBar.vue -->
<template>
  <nav
    class="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-40"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex justify-between h-16">
        <!-- Left side - Logo and Navigation -->
        <div class="flex">
          <!-- Logo -->
          <div class="flex-shrink-0 flex items-center">
            <router-link
              :to="
                authStore.isAdmin ? '/admin/dashboard' : '/employee/dashboard'
              "
              class="flex items-center"
            >
              <div
                class="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3"
              >
                <svg
                  class="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zM2 14.43V16a2 2 0 002 2h12a2 2 0 002-2v-1.57c-1.17.38-2.37.7-3.6.95-1.64.34-3.32.55-5.05.55-1.73 0-3.41-.21-5.05-.55A24.89 24.89 0 012 14.43zM9 7a1 1 0 011-1h0a1 1 0 011 1v0a1 1 0 01-1 1h0a1 1 0 01-1-1v0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
              <span class="font-bold text-xl text-gray-900">ExoTrack</span>
            </router-link>
          </div>

          <!-- Navigation Links -->
          <div class="hidden sm:ml-6 sm:flex sm:space-x-8">
            <!-- Employee Navigation -->
            <template v-if="authStore.isEmployee">
              <router-link
                to="/employee/dashboard"
                class="nav-link"
                :class="{
                  'nav-link-active': $route.name === 'EmployeeDashboard',
                }"
              >
                <Home class="w-4 h-4 mr-2" />
                Dashboard
              </router-link>

              <router-link
                to="/employee/clock"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'EmployeeClock' }"
              >
                <Clock class="w-4 h-4 mr-2" />
                Clock In/Out
              </router-link>

              <router-link
                to="/history"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'History' }"
              >
                <Calendar class="w-4 h-4 mr-2" />
                History
              </router-link>

              <router-link
                to="/employee/requests"
                class="nav-link"
                :class="{
                  'nav-link-active': $route.name === 'EmployeeRequests',
                }"
              >
                <FileText class="w-4 h-4 mr-2" />
                Leave Requests
              </router-link>
            </template>

            <!-- Admin Navigation -->
            <template v-if="authStore.isAdmin">
              <router-link
                to="/admin/dashboard"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'AdminDashboard' }"
              >
                <BarChart3 class="w-4 h-4 mr-2" />
                Dashboard
              </router-link>

              <router-link
                to="/admin/employees"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'AdminEmployees' }"
              >
                <Users class="w-4 h-4 mr-2" />
                Employees
              </router-link>

              <router-link
                to="/attendance"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'Attendance' }"
              >
                <Clock class="w-4 h-4 mr-2" />
                Attendance
              </router-link>

              <router-link
                to="/admin/leave-management"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'AdminLeaveManagement' }"
              >
                <Calendar class="w-4 h-4 mr-2" />
                Leave Management
              </router-link>

              <router-link
                to="/admin/leave-balances"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'AdminLeaveBalances' }"
              >
                <UserCheck class="w-4 h-4 mr-2" />
                Leave Balances
              </router-link>

              <router-link
                to="/admin/holidays"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'AdminHolidays' }"
              >
                <CalendarDays class="w-4 h-4 mr-2" />
                Holidays
              </router-link>

              <router-link
                to="/admin/overtime"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'AdminOvertime' }"
              >
                <ClockIcon class="w-4 h-4 mr-2" />
                Overtime
              </router-link>

              <router-link
                to="/admin/reports"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'AdminReports' }"
              >
                <FileText class="w-4 h-4 mr-2" />
                Reports
              </router-link>
            </template>
          </div>
        </div>

        <!-- Right side - User menu and notifications -->
        <div class="hidden sm:ml-6 sm:flex sm:items-center">
          <!-- Real-time Clock -->
          <div class="mr-4 text-sm text-gray-600">
            {{ currentTime }}
          </div>

          <!-- Notifications (Admin only) -->
          <div v-if="authStore.isAdmin" class="relative mr-4">
            <button
              @click="showNotifications = !showNotifications"
              class="relative p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <Bell class="w-6 h-6" />
              <span
                v-if="notificationCount > 0"
                class="absolute -top-1 -right-1 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full"
              >
                {{ notificationCount > 99 ? "99+" : notificationCount }}
              </span>
            </button>

            <!-- Notifications Dropdown -->
            <div
              v-if="showNotifications"
              class="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
              @click.stop
            >
              <div class="px-4 py-2 border-b border-gray-200">
                <h3 class="text-sm font-medium text-gray-900">Notifications</h3>
              </div>
              <div class="max-h-64 overflow-y-auto">
                <div
                  v-for="notification in notifications"
                  :key="notification.id"
                  class="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  @click="handleNotificationClick(notification)"
                >
                  <div class="flex items-start">
                    <div class="flex-shrink-0">
                      <div
                        class="w-2 h-2 rounded-full mt-2"
                        :class="
                          notification.read ? 'bg-gray-300' : 'bg-blue-500'
                        "
                      ></div>
                    </div>
                    <div class="ml-3 flex-1">
                      <p class="text-sm font-medium text-gray-900">
                        {{ notification.title }}
                      </p>
                      <p class="text-xs text-gray-500 mt-1">
                        {{ notification.message }}
                      </p>
                      <p class="text-xs text-gray-400 mt-1">
                        {{ formatNotificationTime(notification.createdAt) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  v-if="notifications.length === 0"
                  class="px-4 py-6 text-center text-gray-500"
                >
                  No new notifications
                </div>
              </div>
              <div class="px-4 py-2 border-t border-gray-200">
                <button
                  @click="markAllAsRead"
                  class="text-xs text-blue-600 hover:text-blue-800"
                >
                  Mark all as read
                </button>
              </div>
            </div>
          </div>

          <!-- User Profile Dropdown -->
          <div class="relative">
            <button
              @click="showUserMenu = !showUserMenu"
              class="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <div class="flex items-center">
                <!-- User Avatar -->
                <div
                  class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mr-2"
                >
                  <span class="text-white text-sm font-medium">
                    {{ userInitials }}
                  </span>
                </div>
                <!-- User Info -->
                <div class="hidden md:block text-left mr-2">
                  <div class="text-sm font-medium text-gray-900">
                    {{ authStore.userName }}
                  </div>
                  <div class="flex items-center text-xs text-gray-500">
                    <span class="capitalize mr-2">{{ authStore.userRole }}</span>
                    <!-- Clock Status Indicator for Employees -->
                    <div v-if="authStore.isEmployee" class="flex items-center">
                      <div 
                        :class="[
                          'w-2 h-2 rounded-full mr-1',
                          attendanceStore.isActive ? 'bg-green-400' : 'bg-gray-400'
                        ]"
                      ></div>
                      <span :class="attendanceStore.isActive ? 'text-green-600' : 'text-gray-500'">
                        {{ attendanceStore.isActive ? 'Clocked In' : 'Clocked Out' }}
                      </span>
                    </div>
                  </div>
                </div>
                <!-- Dropdown Arrow -->
                <ChevronDown class="w-4 h-4 text-gray-400" />
              </div>
            </button>

            <!-- User Menu Dropdown -->
            <div
              v-if="showUserMenu"
              class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200"
              @click.stop
            >
              <router-link
                to="/profile"
                class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                @click="showUserMenu = false"
              >
                <User class="w-4 h-4 inline mr-2" />
                Profile Settings
              </router-link>

              <button
                @click="handleLogout"
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <LogOut class="w-4 h-4 inline mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>

        <!-- Mobile menu button -->
        <div class="sm:hidden flex items-center">
          <button
            @click="showMobileMenu = !showMobileMenu"
            class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Menu v-if="!showMobileMenu" class="block h-6 w-6" />
            <X v-else class="block h-6 w-6" />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div v-if="showMobileMenu" class="sm:hidden">
      <div class="pt-2 pb-3 space-y-1 bg-white border-t border-gray-200">
        <!-- Mobile User Info -->
        <div class="px-4 py-3 border-b border-gray-200">
          <div class="flex items-center">
            <div
              class="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-3"
            >
              <span class="text-white font-medium">{{ userInitials }}</span>
            </div>
            <div>
              <div class="text-base font-medium text-gray-900">
                {{ authStore.userName }}
              </div>
              <div class="flex items-center text-sm text-gray-500">
                <span class="capitalize mr-2">{{ authStore.userRole }}</span>
                <!-- Clock Status Indicator for Employees (Mobile) -->
                <div v-if="authStore.isEmployee" class="flex items-center">
                  <div 
                    :class="[
                      'w-2 h-2 rounded-full mr-1',
                      attendanceStore.isActive ? 'bg-green-400' : 'bg-gray-400'
                    ]"
                  ></div>
                  <span :class="attendanceStore.isActive ? 'text-green-600' : 'text-gray-500'">
                    {{ attendanceStore.isActive ? 'Clocked In' : 'Clocked Out' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Navigation Links -->
        <template v-if="authStore.isEmployee">
          <router-link
            to="/employee/dashboard"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <Home class="w-5 h-5 mr-3" />
            Dashboard
          </router-link>

          <router-link
            to="/employee/clock"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <Clock class="w-5 h-5 mr-3" />
            Clock In/Out
          </router-link>

          <router-link
            to="/history"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <Calendar class="w-5 h-5 mr-3" />
            History
          </router-link>

          <router-link
            to="/employee/requests"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <FileText class="w-5 h-5 mr-3" />
            Leave Requests
          </router-link>
        </template>

        <template v-if="authStore.isAdmin">
          <router-link
            to="/admin/dashboard"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <BarChart3 class="w-5 h-5 mr-3" />
            Dashboard
          </router-link>

          <router-link
            to="/admin/employees"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <Users class="w-5 h-5 mr-3" />
            Employees
          </router-link>

          <router-link
            to="/attendance"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <Clock class="w-5 h-5 mr-3" />
            Attendance
          </router-link>

          <router-link
            to="/admin/leave-management"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <Calendar class="w-5 h-5 mr-3" />
            Leave Management
          </router-link>

          <router-link
            to="/admin/leave-balances"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <UserCheck class="w-5 h-5 mr-3" />
            Leave Balances
          </router-link>

          <router-link
            to="/admin/holidays"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <CalendarDays class="w-5 h-5 mr-3" />
            Holidays
          </router-link>

          <router-link
            to="/admin/overtime"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <ClockIcon class="w-5 h-5 mr-3" />
            Overtime
          </router-link>

          <router-link
            to="/admin/reports"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <FileText class="w-5 h-5 mr-3" />
            Reports
          </router-link>
        </template>

        <!-- Mobile Menu Footer -->
        <div class="pt-4 pb-3 border-t border-gray-200">
          <router-link
            to="/profile"
            class="mobile-nav-link"
            @click="showMobileMenu = false"
          >
            <User class="w-5 h-5 mr-3" />
            Profile Settings
          </router-link>

          <button
            @click="handleLogout"
            class="mobile-nav-link w-full text-left"
          >
            <LogOut class="w-5 h-5 mr-3" />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useAttendanceStore } from "@/stores/attendance";
import {
  Home,
  Clock,
  Calendar,
  FileText,
  BarChart3,
  Users,
  Bell,
  User,
  LogOut,
  ChevronDown,
  Menu,
  X,
  UserCheck,
  CalendarDays,
  ClockIcon,
} from "lucide-vue-next";

// Stores and router
const authStore = useAuthStore();
const attendanceStore = useAttendanceStore();
const router = useRouter();

// State
const showUserMenu = ref(false);
const showMobileMenu = ref(false);
const showNotifications = ref(false);
const currentTime = ref("");
const notifications = ref([
  {
    id: 1,
    title: "Late Check-in Alert",
    message: "John Doe checked in 15 minutes late",
    createdAt: new Date(Date.now() - 10 * 60 * 1000), // 10 minutes ago
    read: false,
    type: "warning",
  },
  {
    id: 2,
    title: "Leave Request",
    message: "Sarah Johnson requested leave for tomorrow",
    createdAt: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
    read: false,
    type: "info",
  },
  {
    id: 3,
    title: "Monthly Report Ready",
    message: "October attendance report is ready for review",
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    read: true,
    type: "success",
  },
]);

// Computed
const userInitials = computed(() => {
  if (!authStore.user) return "U";
  
  // Handle both single name field and firstName/lastName fields
  const fullName = authStore.user.name || authStore.userName || "";
  const firstName = authStore.user.firstName || "";
  const lastName = authStore.user.lastName || "";
  
  if (firstName && lastName) {
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  } else if (fullName) {
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
  }
  
  return "U";
});

const notificationCount = computed(() => {
  return notifications.value.filter((n) => !n.read).length;
});

// Methods
const updateTime = () => {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString("en-US", {
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
  });
};

const handleLogout = async () => {
  showUserMenu.value = false;
  showMobileMenu.value = false;

  try {
    console.log('🚪 Initiating navbar logout...');
    await authStore.logout();
    console.log('✅ Navbar logout successful');
    
    // Small delay to ensure all cleanup completes
    setTimeout(() => {
      // Force page reload to completely clear app state
      window.location.href = "/login";
      window.location.reload();
    }, 100);
  } catch (error) {
    console.error("❌ Navbar logout error:", error);
    // Force redirect and reload even if logout fails
    setTimeout(() => {
      window.location.href = "/login";
      window.location.reload();
    }, 100);
  }
};

const handleNotificationClick = (notification) => {
  // Mark as read
  notification.read = true;

  // Handle different notification types
  switch (notification.type) {
    case "warning":
      router.push("/attendance");
      break;
    case "info":
      router.push("/admin/employees");
      break;
    case "success":
      router.push("/admin/reports");
      break;
    default:
      break;
  }

  showNotifications.value = false;
};

const markAllAsRead = () => {
  notifications.value.forEach((notification) => {
    notification.read = true;
  });
};

const formatNotificationTime = (date) => {
  const now = new Date();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const handleClickOutside = (event) => {
  // Close dropdowns when clicking outside
  if (!event.target.closest(".relative")) {
    showUserMenu.value = false;
    showNotifications.value = false;
  }
};

// Lifecycle
onMounted(() => {
  updateTime();
  const timeInterval = setInterval(updateTime, 1000);

  // Add click outside listener
  document.addEventListener("click", handleClickOutside);

  // Cleanup interval on unmount
  onUnmounted(() => {
    clearInterval(timeInterval);
    document.removeEventListener("click", handleClickOutside);
  });
});
</script>
